import { useEffect, useMemo, useRef } from 'react';
import { DateRangePicker as VanillaRangePicker, type DateRangePickerOptions } from 'vanillajs-datepicker';

export interface DateRangeValue {
  start: string;
  end: string;
}

export interface DateRangePickerProps {
  /** Range start as a formatted string (matches `format`). */
  start: string;
  /** Range end as a formatted string (matches `format`). */
  end: string;
  onChange: (value: DateRangeValue) => void;
  /** Display/parse format (vanillajs-datepicker tokens). Default `yyyy-mm-dd`. */
  format?: string;
  /** When false, blank side is auto-filled with the other side's date. Default true. */
  allowOneSidedRange?: boolean;
  minDate?: string | Date | number | null;
  maxDate?: string | Date | number | null;
  startPlaceholder?: string;
  endPlaceholder?: string;
  icon?: string;
  disabled?: boolean;
  /**
   * Where to append the dropdown calendars. Defaults to `document.body` so they overlay
   * scrolling/stacking containers (e.g. a sidebar).
   */
  container?: HTMLElement | string;
  className?: string;
}

/**
 * React wrapper around vanillajs-datepicker's DateRangePicker (two linked inputs, styled by the
 * Limitless theme). Controlled via `start`/`end`/`onChange`; date-only.
 */
export function DateRangePicker({
  start,
  end,
  onChange,
  format = 'yyyy-mm-dd',
  allowOneSidedRange = true,
  minDate,
  maxDate,
  startPlaceholder = 'From',
  endPlaceholder = 'To',
  icon = 'ph-calendar',
  disabled,
  container,
  className = '',
}: DateRangePickerProps) {
  const elRef = useRef<HTMLDivElement>(null);
  const rpRef = useRef<VanillaRangePicker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const settingRef = useRef(false);

  const options = useMemo<DateRangePickerOptions>(
    () => ({
      format,
      buttonClass: 'btn',
      prevArrow: '←',
      nextArrow: '→',
      allowOneSidedRange,
      // Append the popovers to <body> so they aren't clipped/stacked under scrolling containers.
      container: container ?? document.body,
      ...(minDate != null && { minDate }),
      ...(maxDate != null && { maxDate }),
    }),
    [format, allowOneSidedRange, container, minDate, maxDate],
  );

  const read = (): DateRangeValue => {
    const dates = rpRef.current?.getDates(format) ?? [];
    const toStr = (d: Date | string | undefined) => (typeof d === 'string' ? d : '');
    return { start: toStr(dates[0]), end: toStr(dates[1]) };
  };

  // Instantiate once.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const rp = new VanillaRangePicker(el, options);
    rpRef.current = rp;
    if (start || end) {
      settingRef.current = true;
      rp.setDates(start || null, end || null);
      settingRef.current = false;
    }
    const handler = () => {
      if (settingRef.current) return;
      onChangeRef.current(read());
    };
    el.addEventListener('changeDate', handler);
    return () => {
      el.removeEventListener('changeDate', handler);
      rp.destroy();
      rpRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    rpRef.current?.setOptions(options);
  }, [options]);

  // Reflect external value changes.
  useEffect(() => {
    const rp = rpRef.current;
    if (!rp) return;
    const cur = read();
    if (cur.start === start && cur.end === end) return;
    settingRef.current = true;
    rp.setDates(start || null, end || null);
    settingRef.current = false;
  }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={elRef} className={`input-group ${className}`.trim()}>
      <span className="input-group-text">
        <i className={icon} />
      </span>
      <input type="text" className="form-control" placeholder={startPlaceholder} disabled={disabled} defaultValue={start} />
      <span className="input-group-text">–</span>
      <input type="text" className="form-control" placeholder={endPlaceholder} disabled={disabled} defaultValue={end} />
    </div>
  );
}
